/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Maybe, CareerRole_PathwayLevel as PathwayLevel } from '__generated__/graphql-types';

import Imgix from 'js/components/Imgix';

import { CardInteractiveArea, Grid, Link, Skeleton, Typography2, VisuallyHidden } from '@coursera/cds-core';
import type { Carousel, Recommender } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { useIntl } from 'bundles/page/lib/useIntl';
import type { PathwaySpecializationFragment } from 'bundles/unified-common/queries/__generated__/CareerRolesCollection';

import _t from 'i18n!nls/unified-common';

type CareerRoleCardCredentialsProps = {
  credentials?: Maybe<Pick<PathwaySpecializationFragment, 'name' | 'slug' | 'id' | 'partners' | 'urlPath'>[]>;
  linkVariant?: 'bodyPrimary' | 'bodySecondary';
  roleName?: Maybe<string>;
  roleSlug?: string;
  eventingData?: {
    carousel?: Carousel;
    recommender?: Recommender;
  };
  proficiencyLevel?: PathwayLevel;
  showPathwaysLoadingState?: boolean;
};

const style = {
  partnerLogo: css`
    display: inline-flex;
    flex-shrink: 0;
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: 4px;
    height: 24px;
    width: 24px;
    padding: var(--cds-spacing-50);
    margin-right: var(--cds-spacing-100);
    align-items: center;

    img {
      width: 100%;
    }
  `,
  credential: css`
    display: flex;
  `,
  viewRdpCTA: css`
    padding-left: var(--cds-spacing-100);
  `,
  loadingStateContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
  `,
};

const CareerCardCredentials: React.FC<CareerRoleCardCredentialsProps> = ({
  credentials,
  linkVariant = 'bodyPrimary',
  roleName,
  roleSlug,
  proficiencyLevel,
  eventingData,
  showPathwaysLoadingState,
}) => {
  const track = useTracker();
  const intl = useIntl();

  if (!roleSlug) {
    return null;
  }

  const createHref = () => {
    const searchParams = new URLSearchParams();

    if (proficiencyLevel) {
      searchParams.set('level', proficiencyLevel.toLowerCase());
    }

    if (eventingData?.carousel?.id) {
      searchParams.set('recommenderId', eventingData.carousel.id);
    }

    const queryString = searchParams.toString();
    return `/career-academy/roles/${roleSlug}${queryString ? `?${queryString}` : ''}`;
  };

  return (
    <Grid container spacing={8} direction="column">
      <Grid item>
        <Typography2 aria-label={_t('Credentials: ')} component="h3" variant="subtitleMedium">
          {_t('Credentials')}
        </Typography2>
      </Grid>
      {!credentials?.length && showPathwaysLoadingState && (
        <Grid item>
          <div css={style.loadingStateContainer}>
            <Skeleton height={16} variant="rect" width="90%" />
            <Skeleton height={16} variant="rect" width="70%" />
          </div>
        </Grid>
      )}
      {credentials?.slice(0, 2).map((cred, index) => {
        return (
          <Grid key={cred.slug} item alignItems="center">
            <CardInteractiveArea>
              <Link
                variant="quiet"
                typographyVariant={linkVariant}
                href={cred.urlPath}
                onClick={(event) => {
                  // prevent the tracking event to bubble up to the role card
                  event.stopPropagation();

                  track('click_button', {
                    button: {
                      linkURL: cred.urlPath,
                      linkName: 'role_card_xdp_link',
                      linkType: 'internal',
                      name: 'program_info',
                    },
                    product: {
                      id: cred.id,
                      slug: cred.slug,
                    },
                    role: {
                      roleSlug,
                    },
                    productCard: {
                      index,
                      recommender: eventingData?.recommender,
                    },
                  });
                }}
              >
                <div css={style.credential}>
                  <div css={style.partnerLogo}>
                    {/* @ts-expect-error URL: string */}
                    <Imgix src={cred.partners[0]?.squareLogo} alt={cred.partners[0]?.name} width={24} />
                  </div>
                  {cred.name}
                  {index === 0 && credentials?.length === 1 ? null : (
                    <VisuallyHidden component="span">,</VisuallyHidden>
                  )}
                </div>
              </Link>
            </CardInteractiveArea>
          </Grid>
        );
      })}
      {credentials && credentials.length > 2 && (
        <CardInteractiveArea>
          <Link
            href={createHref()}
            variant="quiet"
            typographyVariant="bodySecondary"
            css={style.viewRdpCTA}
            aria-label={intl.formatMessage(
              roleName
                ? _t('+ {count} more {count, plural, =1 {credential} other {credentials} } for {roleName}')
                : _t('+ {count} more {count, plural, =1 {credential} other {credentials} }'),
              { count: credentials.length - 2, roleName: roleName ?? '' }
            )}
          >
            {_t('+ #{count} more', { count: credentials.length - 2 })}
          </Link>
        </CardInteractiveArea>
      )}
    </Grid>
  );
};

export default CareerCardCredentials;
